'use client';

import React, { useState, useEffect, Suspense } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { BrainCircuit, Bot, MousePointerClick } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TypingAnimation from "./TypingAnimation";
import { Button } from "@/components/ui/button";

const chatMessages = [
    'Automatize até 85% das mensagens pós-operatórias',
    'e recupere 3–4 horas diárias',
    'em apenas 15 minutos de configuração.',
];

const scrolledUpMessages = [
    "Já que deu uma olhadinha no nosso site...",
    "é hora de testar nosso sistema.",
];

const titleContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.3,
        },
    },
};

const titleLineVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: "easeOut",
        },
    },
};

type HeroProps = {
    isBot: boolean;
    onAnimationComplete: () => void;
};

export default function Hero({ isBot, onAnimationComplete }: HeroProps) {
  const [visibleMessages, setVisibleMessages] = useState(isBot ? chatMessages.length : 0);
  const [isTyping, setIsTyping] = useState(isBot ? false : true);
  const [showButton, setShowButton] = useState(isBot);
  const [showDetails, setShowDetails] = useState(isBot);
  const [hasAnimated, setHasAnimated] = useState(isBot);
  
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const [hasScrolledBackUp, setHasScrolledBackUp] = useState(false);
  
  const [isTypingOnScroll, setIsTypingOnScroll] = useState(false);
  const [visibleScrolledMessages, setVisibleScrolledMessages] = useState(0);

  useEffect(() => {
    if (isBot || hasAnimated) {
        if (isBot) onAnimationComplete();
        return;
    }

    const timeouts: NodeJS.Timeout[] = [];
    
    timeouts.push(setTimeout(() => setIsTyping(true), 500));
    timeouts.push(setTimeout(() => {
        setVisibleMessages(1);
        setIsTyping(false);
    }, 1500));
    timeouts.push(setTimeout(() => setVisibleMessages(2), 2200));
    timeouts.push(setTimeout(() => setVisibleMessages(3), 2900));
    timeouts.push(setTimeout(() => setShowButton(true), 3200));
    timeouts.push(setTimeout(() => {
      setShowDetails(true);
      onAnimationComplete();
      setHasAnimated(true);
    }, 3500));

    return () => {
        timeouts.forEach(clearTimeout);
    };
}, [isBot, onAnimationComplete, hasAnimated]);

  useEffect(() => {
    const handleScroll = () => {
        if (window.scrollY > 500 && !hasScrolledDown) {
            setHasScrolledDown(true);
        }

        if (hasAnimated && hasScrolledDown && window.scrollY < 50 && !hasScrolledBackUp) {
            setHasScrolledBackUp(true);
        }
    };
    
    window.addEventListener('scroll', handleScroll);

    return () => {
        window.removeEventListener('scroll', handleScroll);
    };
}, [hasAnimated, hasScrolledDown, hasScrolledBackUp]);

  useEffect(() => {
    if (hasScrolledBackUp && !isBot) {
      const scrollTimeouts: NodeJS.Timeout[] = [];
      scrollTimeouts.push(setTimeout(() => setIsTypingOnScroll(true), 300));
      scrollTimeouts.push(setTimeout(() => {
          setIsTypingOnScroll(false);
          setVisibleScrolledMessages(1);
      }, 1300));
      scrollTimeouts.push(setTimeout(() => setVisibleScrolledMessages(2), 2100));
      
      return () => {
        scrollTimeouts.forEach(clearTimeout);
      }
    }
  }, [hasScrolledBackUp, isBot]);


  const messageVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };
  
  const detailVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0,
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };
  
  const MainContainer = isBot ? 'div' : motion.div;

  return (
    <section className="group relative w-full flex flex-col items-center justify-center overflow-hidden bg-primary text-primary-foreground">
       {!isBot && <div className="absolute inset-0 w-full h-full bg-primary/80 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] z-0"></div>}
      <div className="container relative z-10 mx-auto px-4 pb-10 pt-4 text-center md:pb-12 md:pt-6">
        <Suspense fallback={<div className="min-h-[480px]" />}>
            <motion.div
              className="mb-8"
              variants={titleContainerVariants}
              initial={isBot ? undefined : "hidden"}
              animate={isBot ? undefined : "visible"}
            >
              <motion.div
                variants={titleLineVariants}
                className="flex justify-center"
              >
                <Image
                  src="/images/logomedfollow.png?v=2"
                  alt="MedFollow Logo"
                  width={600}
                  height={150}
                  className="h-auto max-w-[50vw] sm:max-w-md md:max-w-lg"
                  priority
                />
              </motion.div>
            </motion.div>
          <MainContainer 
              className={`mx-auto max-w-4xl rounded-xl p-4 sm:p-6 ${!isBot ? 'backdrop-blur-sm bg-primary-foreground/5 border border-primary-foreground/10' : ''}`}
          >
            <div className="pt-8">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 pt-1">
                  <Bot className="h-8 w-8 text-primary-foreground/90" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex flex-col gap-3">
                      <AnimatePresence mode="wait">
                        {hasScrolledBackUp && !isBot ? (
                          <>
                              {scrolledUpMessages.slice(0, visibleScrolledMessages).map((msg, i) => (
                                  <motion.div
                                      key={`scrolled-${i}`}
                                      custom={i}
                                      initial="hidden"
                                      animate="visible"
                                      exit="hidden"
                                      variants={messageVariants}
                                      className={'w-fit max-w-[90%] rounded-xl px-4 py-3 bg-[#E9F8FA] text-[#1E2D3A]'}
                                  >
                                      <p className="!font-semibold block text-lg sm:text-xl">{msg}</p>
                                  </motion.div>
                              ))}
                          </>
                        ) : (
                          <>
                            {chatMessages.slice(0, visibleMessages).map((msg, i) => (
                              <motion.div
                                  key={i}
                                  custom={i}
                                  initial="hidden"
                                  animate="visible"
                                  exit="hidden"
                                  variants={messageVariants}
                                  className={'w-fit max-w-[90%] rounded-xl px-4 py-3 bg-[#E9F8FA] text-[#1E2D3A]'}
                              >
                                  <p className="!font-semibold block text-lg sm:text-xl">{msg}</p>
                              </motion.div>
                            ))}
                          </>
                        )}
                      </AnimatePresence>
                    {isTyping && !hasScrolledBackUp && !isBot && <TypingAnimation />}
                    {isTypingOnScroll && !isBot && <TypingAnimation />}
                    {(showButton) && (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={isBot ? undefined : messageVariants}
                        className="w-fit"
                      >
                        <Button size="lg" asChild className="bg-green-300 text-black hover:bg-green-400 h-auto py-3 px-6 sm:px-8 sm:h-11">
                          <Link href="#cta">
                            <MousePointerClick className="mr-2 h-5 w-5 animate-pulse" />
                            Acessar demonstração técnica
                          </Link>
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <AnimatePresence>
              {(showDetails) && (
                <motion.div initial={isBot ? undefined : "hidden"} animate={isBot ? undefined : "visible"} variants={isBot ? undefined : detailVariants}>
                  <p
                    className="mt-8 text-base leading-7 text-primary-foreground md:text-lg tracking-tight"
                  >
                    Sistema de Inteligência Médica Adaptativa via WhatsApp + Plataforma de Gestão: protocolos específicos por cirurgia, alertas inteligentes, agendamento com confirmação automática e documentação completa.
                  </p>

                  <div
                    className="mt-10 pt-10 border-t border-primary-foreground/20"
                  >
                    <div className="relative text-center">
                        <Badge variant="secondary" className="cursor-pointer text-sm font-normal transition-colors hover:bg-secondary/80">
                          <BrainCircuit className="mr-2 h-4 w-4" />
                          Baseado em Inteligência Artificial Médica
                        </Badge>
                        <p className="mt-4 text-sm text-primary-foreground/80 max-w-2xl mx-auto tracking-tight">
                            Um sistema de automação humanizado, que interpreta protocolos cirúrgicos e responde pacientes de forma precisa e segura. Ele resolve automaticamente as dúvidas básicas e repetitivas e aciona o médico apenas em casos críticos, quando há risco real de complicação. Nessas situações, o sistema gera notificação imediata na plataforma, garantindo que o médico ou a equipe sejam alertados sem demora.
                        </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </MainContainer>
        </Suspense>
      </div>
    </section>
  );
}
